import type { ErrorEvent as SentryErrorEvent } from '@sentry/react';

export const MUTATION_WINDOW_SECONDS = 10;
export const MAX_TAG_TALLY_ENTRIES = 10;
export const MAX_DISTINCT_TAGS_PER_BUCKET = 20;

const OTHER_TAG_KEY = '__other';

export const MS_TRANSLATE_ATTRIBUTES = ['_msttexthash', '_istranslated'];
export const IMMERSIVE_TRANSLATE_MARKER_ATTRIBUTES = [
  'data-immersive-translate-walked',
  'data-immersive-translate-paragraph',
  'data-immersive-translate-translation-element-mark',
];

const TRANSLATED_CLASS_PATTERN = /translated-(ltr|rtl)/;
const DOM_INTERFERENCE_MESSAGE_PATTERN = /removeChild|insertBefore/;
const CIRCULAR_JSON_MESSAGE_PATTERN = /circular structure to json/i;
const REACT_FIBER_MARKER = '__reactFiber';
const POSTHOG_COOKIE_PREFIX = 'ph_';

export const MAX_AUTOMATION_LANGUAGES_LENGTH = 64;

const FONT_ELEMENT_SELECTOR = 'font';
const MS_TRANSLATE_MARKER_SELECTOR = MS_TRANSLATE_ATTRIBUTES.map((name) => `[${name}]`).join(', ');
const IMMERSIVE_TRANSLATE_MARKER_SELECTOR = IMMERSIVE_TRANSLATE_MARKER_ATTRIBUTES.map((name) => `[${name}]`).join(', ');

export type MutationTypeCounts = Record<MutationRecordType, number>;

export type TagTally = Record<string, number>;

/**
 * A minimal, structurally-compatible subset of `MutationRecord` — real records satisfy
 * this shape, but tests can construct plain object literals without needing a live
 * MutationObserver (MutationRecord has no public constructor).
 */
export type ObservedMutation = {
  type: MutationRecordType;
  target: Node;
  attributeName?: string | null;
  addedNodes: ArrayLike<Node>;
  removedNodes: ArrayLike<Node>;
};

export type MutationBucket = {
  second: number;
  mutationTypeCounts: MutationTypeCounts;
  addedNodeCount: number;
  removedNodeCount: number;
  addedTagTally: TagTally;
  removedTagTally: TagTally;
  outOfRootMutationCount: number;
};

export type MutationWindow = {
  buckets: Array<MutationBucket>;
};

export type MutationSummary = {
  mutationTypeCounts: MutationTypeCounts;
  addedNodeCount: number;
  removedNodeCount: number;
  addedTagTally: TagTally;
  removedTagTally: TagTally;
  outOfRootMutationCount: number;
};

export const createEmptyMutationWindow = (): MutationWindow => ({ buckets: [] });

export const pruneMutationWindow = (window: MutationWindow, nowSecond: number): MutationWindow => ({
  buckets: window.buckets.filter((bucket) => nowSecond - bucket.second < MUTATION_WINDOW_SECONDS),
});

export const recordMutations = (
  window: MutationWindow,
  mutations: ReadonlyArray<ObservedMutation>,
  rootNode: Node | null,
  nowMs: number,
): MutationWindow => {
  const nowSecond = Math.floor(nowMs / 1000);
  const pruned = pruneMutationWindow(window, nowSecond);
  const existingBucket = pruned.buckets.find((bucket) => bucket.second === nowSecond);
  const bucket = existingBucket ?? createEmptyBucket(nowSecond);

  if (!existingBucket) {
    pruned.buckets.push(bucket);
  }

  mutations.forEach((mutation) => {
    bucket.mutationTypeCounts[mutation.type] += 1;

    if (rootNode && !rootNode.contains(mutation.target)) {
      bucket.outOfRootMutationCount += 1;
    }

    // ArrayLike<Node> has no iterator (kept structural so tests can pass plain arrays), and the
    // es5 target has no downlevelIteration — an index loop is required, not just preferred.
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < mutation.addedNodes.length; i += 1) {
      bucket.addedNodeCount += 1;
      bumpTagTally(bucket.addedTagTally, mutation.addedNodes[i]);
    }

    // eslint-disable-next-line @typescript-eslint/prefer-for-of -- see addedNodes loop above
    for (let i = 0; i < mutation.removedNodes.length; i += 1) {
      bucket.removedNodeCount += 1;
      bumpTagTally(bucket.removedTagTally, mutation.removedNodes[i]);
    }
  });

  return pruned;
};

export const summarizeMutationWindow = (window: MutationWindow, nowMs: number): MutationSummary => {
  const nowSecond = Math.floor(nowMs / 1000);
  const { buckets } = pruneMutationWindow(window, nowSecond);

  const summary: MutationSummary = {
    mutationTypeCounts: createEmptyMutationTypeCounts(),
    addedNodeCount: 0,
    removedNodeCount: 0,
    addedTagTally: {},
    removedTagTally: {},
    outOfRootMutationCount: 0,
  };

  buckets.forEach((bucket) => {
    summary.mutationTypeCounts.attributes += bucket.mutationTypeCounts.attributes;
    summary.mutationTypeCounts.characterData += bucket.mutationTypeCounts.characterData;
    summary.mutationTypeCounts.childList += bucket.mutationTypeCounts.childList;
    summary.addedNodeCount += bucket.addedNodeCount;
    summary.removedNodeCount += bucket.removedNodeCount;
    summary.outOfRootMutationCount += bucket.outOfRootMutationCount;
    mergeTagTally(summary.addedTagTally, bucket.addedTagTally);
    mergeTagTally(summary.removedTagTally, bucket.removedTagTally);
  });

  return {
    ...summary,
    addedTagTally: topTags(summary.addedTagTally),
    removedTagTally: topTags(summary.removedTagTally),
  };
};

export type TranslationFingerprints = {
  fontElementAdded: boolean;
  msTranslateAttributeSeen: boolean;
  immersiveTranslateAttributeSeen: boolean;
  htmlLangChangedAfterLoad: boolean;
  htmlClassTranslated: boolean;
};

export type TranslationDetectionContext = {
  htmlElement: Element;
  htmlLangAtInit: string | null;
};

export const createEmptyTranslationFingerprints = (): TranslationFingerprints => ({
  fontElementAdded: false,
  msTranslateAttributeSeen: false,
  immersiveTranslateAttributeSeen: false,
  htmlLangChangedAfterLoad: false,
  htmlClassTranslated: false,
});

export const detectTranslationFingerprints = (
  mutations: ReadonlyArray<ObservedMutation>,
  current: TranslationFingerprints,
  context: TranslationDetectionContext,
): TranslationFingerprints => {
  const flags = { ...current };

  mutations.forEach((mutation) => {
    detectAddedNodeFingerprints(mutation, flags);
    detectAttributeFingerprints(mutation, flags, context);
  });

  return flags;
};

export const matchesDomInterferenceSignature = (event: SentryErrorEvent): boolean => {
  const values = event.exception?.values ?? [];

  return values.some((value) => {
    if (value.type === 'NotFoundError') {
      return true;
    }

    const message = value.value ?? '';

    if (DOM_INTERFERENCE_MESSAGE_PATTERN.test(message)) {
      return true;
    }

    return (
      value.type === 'TypeError' && CIRCULAR_JSON_MESSAGE_PATTERN.test(message) && message.includes(REACT_FIBER_MARKER)
    );
  });
};

const ERROR_BOUNDARY_TAG_KEY = 'error_boundary';

/**
 * Any crash a self-tagging React error boundary catches destroys the UI the same way a
 * DOM-interference crash does, so it earns the same forensics without needing its own
 * signature — this is what keeps `matchesDomInterferenceSignature` from growing forever.
 */
export const shouldAttachDomInterferenceContext = (event: SentryErrorEvent): boolean => {
  const boundaryTag = event.tags?.[ERROR_BOUNDARY_TAG_KEY];

  if (typeof boundaryTag === 'string' && boundaryTag !== '') {
    return true;
  }

  return matchesDomInterferenceSignature(event);
};

export const isDomInterferenceSuspected = (params: {
  translationFingerprints: TranslationFingerprints;
  outOfRootMutationCount: number;
  webdriver?: boolean;
}): boolean => {
  const hasTranslationFingerprint = Object.values(params.translationFingerprints).some(Boolean);

  return hasTranslationFingerprint || params.outOfRootMutationCount > 0 || params.webdriver === true;
};

export type EnvironmentSnapshot = {
  navigatorLanguage: string | null;
  navigatorLanguages: Array<string>;
  htmlLangAtInit: string | null;
  htmlLangAtSnapshot: string | null;
  htmlTranslateAttribute: string | null;
};

export type LastUiActionSnapshot = {
  analyticsId: string;
  pathname: string;
  timestamp: number;
  ageMs: number;
};

export type AutomationFingerprint = {
  webdriver: boolean;
  virgin_storage_at_boot: boolean | null;
  languages: string | null;
  screen: string | null;
  viewport: string | null;
  hardware_concurrency: number | null;
  max_touch_points: number | null;
  plugins_count: number | null;
  visibility_state: string | null;
};

export type DomInterferenceSnapshot = MutationSummary &
  AutomationFingerprint & {
    translationFingerprints: TranslationFingerprints;
    environment: EnvironmentSnapshot;
    lastUiAction: LastUiActionSnapshot | null;
  };

export const safely = <T>(getValue: () => T, fallback: T): T => {
  try {
    return getValue();
  } catch {
    return fallback;
  }
};

/**
 * Storage access can throw in privacy modes (Safari ITP, locked-down browser profiles);
 * must run before anything else in the app writes to localStorage/cookies.
 */
export const computeVirginStorageAtBoot = (): boolean | null =>
  safely(() => {
    const hasEmptyLocalStorage = window.localStorage.length === 0;
    const hasPostHogCookie = document.cookie
      .split(';')
      .some((cookiePair) => cookiePair.trim().startsWith(POSTHOG_COOKIE_PREFIX));

    return hasEmptyLocalStorage && !hasPostHogCookie;
  }, null);

export const buildAutomationFingerprint = (virginStorageAtBoot: boolean | null): AutomationFingerprint => ({
  webdriver: safely(() => navigator.webdriver === true, false),
  virgin_storage_at_boot: virginStorageAtBoot,
  languages: safely(() => (navigator.languages ?? []).join(',').slice(0, MAX_AUTOMATION_LANGUAGES_LENGTH), null),
  screen: safely(() => `${screen.width}x${screen.height}`, null),
  viewport: safely(() => `${window.innerWidth}x${window.innerHeight}`, null),
  hardware_concurrency: safely(() => navigator.hardwareConcurrency ?? null, null),
  max_touch_points: safely(() => navigator.maxTouchPoints ?? null, null),
  plugins_count: safely(() => navigator.plugins?.length ?? null, null),
  visibility_state: safely(() => document.visibilityState ?? null, null),
});

const createEmptyMutationTypeCounts = (): MutationTypeCounts => ({
  attributes: 0,
  characterData: 0,
  childList: 0,
});

const createEmptyBucket = (second: number): MutationBucket => ({
  second,
  mutationTypeCounts: createEmptyMutationTypeCounts(),
  addedNodeCount: 0,
  removedNodeCount: 0,
  addedTagTally: {},
  removedTagTally: {},
  outOfRootMutationCount: 0,
});

const bumpTagTally = (tally: TagTally, node: Node) => {
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const tagName = (node as Element).tagName.toLowerCase();
  const key = tagName in tally || Object.keys(tally).length < MAX_DISTINCT_TAGS_PER_BUCKET ? tagName : OTHER_TAG_KEY;

  tally[key] = (tally[key] ?? 0) + 1;
};

const mergeTagTally = (target: TagTally, source: TagTally) => {
  Object.entries(source).forEach(([tag, count]) => {
    target[tag] = (target[tag] ?? 0) + count;
  });
};

const topTags = (tally: TagTally): TagTally =>
  Object.fromEntries(
    Object.entries(tally)
      .sort((a, b) => b[1] - a[1])
      .slice(0, MAX_TAG_TALLY_ENTRIES),
  );

const elementOrSubtreeMatches = (element: Element, selector: string): boolean =>
  element.matches(selector) || element.querySelector(selector) !== null;

const detectAddedNodeFingerprints = (mutation: ObservedMutation, flags: TranslationFingerprints): void => {
  // eslint-disable-next-line @typescript-eslint/prefer-for-of -- see recordMutations' addedNodes loop
  for (let i = 0; i < mutation.addedNodes.length; i += 1) {
    const node = mutation.addedNodes[i];
    if (node.nodeType !== Node.ELEMENT_NODE) {
      continue;
    }

    const element = node as Element;

    if (!flags.fontElementAdded && elementOrSubtreeMatches(element, FONT_ELEMENT_SELECTOR)) {
      flags.fontElementAdded = true;
    }
    if (!flags.msTranslateAttributeSeen && elementOrSubtreeMatches(element, MS_TRANSLATE_MARKER_SELECTOR)) {
      flags.msTranslateAttributeSeen = true;
    }
    if (
      !flags.immersiveTranslateAttributeSeen &&
      elementOrSubtreeMatches(element, IMMERSIVE_TRANSLATE_MARKER_SELECTOR)
    ) {
      flags.immersiveTranslateAttributeSeen = true;
    }
  }
};

const detectAttributeFingerprints = (
  mutation: ObservedMutation,
  flags: TranslationFingerprints,
  context: TranslationDetectionContext,
): void => {
  if (mutation.type !== 'attributes') {
    return;
  }

  const attributeName = mutation.attributeName ?? '';

  if (!flags.msTranslateAttributeSeen && MS_TRANSLATE_ATTRIBUTES.includes(attributeName)) {
    flags.msTranslateAttributeSeen = true;
  }
  if (!flags.immersiveTranslateAttributeSeen && IMMERSIVE_TRANSLATE_MARKER_ATTRIBUTES.includes(attributeName)) {
    flags.immersiveTranslateAttributeSeen = true;
  }

  if (mutation.target !== context.htmlElement) {
    return;
  }

  if (
    !flags.htmlLangChangedAfterLoad &&
    attributeName === 'lang' &&
    context.htmlElement.getAttribute('lang') !== context.htmlLangAtInit
  ) {
    flags.htmlLangChangedAfterLoad = true;
  }
  if (
    !flags.htmlClassTranslated &&
    attributeName === 'class' &&
    TRANSLATED_CLASS_PATTERN.test(context.htmlElement.getAttribute('class') ?? '')
  ) {
    flags.htmlClassTranslated = true;
  }
};
